import { useMemo, useState } from "react";
import { dramas, dramaGenres } from "@/mocks/dramas";

export default function DramaSection() {
  const [activeGenre, setActiveGenre] = useState("全部");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return dramas.filter((item) => {
      const matchGenre = activeGenre === "全部" || item.genre === activeGenre;
      const matchKeyword =
        keyword === "" ||
        item.title.toLowerCase().includes(keyword) ||
        item.genre.toLowerCase().includes(keyword) ||
        item.tags.some((tag) => tag.toLowerCase().includes(keyword));
      return matchGenre && matchKeyword;
    });
  }, [activeGenre, query]);

  return (
    <section id="hot" className="bg-background-50">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-primary-600">
              <i className="ri-fire-line"></i>
              热门推荐
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              值得一看的热门韩剧
            </h2>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              下面按类型整理了 9 部讨论度较高的剧目，涵盖悬疑惊悚、浪漫爱情、青春治愈等方向。
              你可以直接切换类型标签，或用关键词筛选片单，快速锁定想看的剧。
            </p>
          </div>

          <div className="w-full max-w-md">
            <label className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5">
              <i className="ri-search-line text-base text-foreground-500"></i>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索剧名、类型或看点"
                className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-500"
              />
              {query && (
                <button
                  type="button"
                  aria-label="清除搜索"
                  onClick={() => setQuery("")}
                  className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full text-foreground-500 hover:bg-background-100"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </label>
          </div>
        </div>

        <div className="mt-7 flex flex-wrap gap-2">
          {dramaGenres.map((genre) => (
            <button
              key={genre}
              type="button"
              onClick={() => setActiveGenre(genre)}
              aria-pressed={activeGenre === genre}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                activeGenre === genre
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-200 bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {genre}
            </button>
          ))}
        </div>

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((item) => (
              <article
                key={item.id}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50"
              >
                <div className="relative h-52 w-full overflow-hidden bg-background-200">
                  <img
                    src={item.image}
                    alt={`${item.title} 韩剧在线观看封面`}
                    title={`${item.title} 在线观看`}
                    className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-background-50/90 px-3 py-1 text-xs font-medium text-primary-700 backdrop-blur">
                    {item.genre}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                    <span className="shrink-0 text-xs text-foreground-600">{item.year}</span>
                  </div>
                  <p className="mt-3 flex-1 text-sm leading-6 text-foreground-700">{item.summary}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {item.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-600">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <p className="mt-4 text-sm font-medium text-foreground-900">没有找到匹配的剧目</p>
            <p className="mt-1 text-xs text-foreground-600">换个关键词，或点击“全部”查看完整片单。</p>
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setActiveGenre("全部");
              }}
              className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}